import { Modal, ModalBody } from '@readcloud/component-library';
import { homeSettingsSelectors } from '@readcloud/state';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Large,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
} from '@readcloud/ui';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useCloudsAndContentActions } from '../context/ActionsContext';
import { getSubdomainAssets } from '@readcloud/common/lib/utils/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

export interface SettingsModalProps {
  open: boolean;
  onToggle: () => void;
}

const FormSchema = z.object({
  myPurchasesCloudPosition: z.enum(['normal', 'top', 'bottom', 'hide']),
  myInteractiveLoginsPosition: z.enum(['normal', 'top', 'bottom', 'hide']),
  tileSize: z.enum(['small', 'medium', 'large']),
  hideEmptyClouds: z.boolean(),
  hideTitle: z.boolean(),
});

export const SettingsModal: React.FC<SettingsModalProps> = (props) => {
  const settings = useSelector(homeSettingsSelectors.getSettings);
  const actions = useCloudsAndContentActions();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    values: settings,
  });
  const { open, onToggle } = props;
  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    onToggle();
    actions.updateSettings(data);
  };

  const {name} = getSubdomainAssets();

  return (
    <div>
      <Modal isOpen={open} toggle={onToggle}>
        <ModalBody className="rc-flex rc-flex-col rc-gap-4">
          <Large>Settings</Large>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="rc-space-y-6"
            >
              <FormField
                control={form.control}
                name="myPurchasesCloudPosition"
                render={({ field }): React.ReactElement => (
                  <FormItem>
                    <FormLabel>{name.MY_PURCHASE} Cloud Position</FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={`${name.MY_PURCHASE} Cloud Position`} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rc-z-[2100] rc-bg-background rc-max-h-[var(--radix-popper-available-height)] rc-rounded">
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="top">Top</SelectItem>
                        <SelectItem value="bottom">Bottom</SelectItem>
                        <SelectItem value="hide">Hide</SelectItem>
                      </SelectContent>
                    </Select>

                    <FormDescription>
                      Select a position for the {name.MY_PURCHASE} Cloud.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="myInteractiveLoginsPosition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{name.MY_INTERACTIVE_LOGINS} Position</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={`${name.MY_PURCHASE} Cloud Position`} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className=" rc-bg-background  rc-z-[2100] rc-max-h-[var(--radix-popper-available-height)] rc-rounded">
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="top">Top</SelectItem>
                        <SelectItem value="bottom">Bottom</SelectItem>
                        <SelectItem value="hide">Hide</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select a position for the {name.MY_INTERACTIVE_LOGINS} Cloud.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tileSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tile Size</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Tile Size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rc-z-[2100] rc-bg-background rc-max-h-[var(--radix-popper-available-height)] rc-rounded">
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select a size for the tiles.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hideEmptyClouds"
                render={({ field }) => (
                  <FormItem className="rc-flex rc-items-center rc-justify-between">
                    <FormLabel>Hide Empty Clouds</FormLabel>
                    <FormControl id="hideEmptyClouds">
                      <Switch
                        id="hideEmptyClouds"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hideTitle"
                render={({ field }) => (
                  <FormItem
                    id="hideTitle"
                    className="rc-flex rc-items-center rc-justify-between"
                  >
                    <FormLabel>Hide Book Titles</FormLabel>
                    <FormControl id="hideTitle">
                      <Switch
                        id="hideTitle"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Separator />
              <div className="rc-flex rc-justify-end rc-gap-2">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={(e) => onToggle()}
                >
                  Cancel
                </Button>
                <Button variant="default" type="submit">
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
};
